import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Mail,
  X,
  ChevronDown,
  ChevronRight,
  GripVertical,
} from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'

const FIELD_TYPES = [
  { name: 'text', label: 'Text Input' },
  { name: 'textarea', label: 'Text Area' },
  { name: 'select', label: 'Select' },
  { name: 'multiselect', label: 'Multi Select' },
  { name: 'boolean', label: 'Checkbox' },
]

const getFieldTypeColor = (type) => {
  const colors = {
    text: 'bg-blue-100 text-blue-800',
    textarea: 'bg-green-100 text-green-800',
    select: 'bg-indigo-100 text-indigo-800',
    multiselect: 'bg-violet-100 text-violet-800',
    boolean: 'bg-yellow-100 text-yellow-800',
  }
  return colors[type] || 'bg-gray-100 text-gray-800'
}

export default function Forms() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newFormData, setNewFormData] = useState({
    name: '',
    slug: '',
    fields: [],
  })
  const [expandedFields, setExpandedFields] = useState(new Set())
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: forms, isLoading } = useQuery({
    queryKey: ['forms'],
    queryFn: () => api.get('/forms').then((res) => res.data),
  })

  const createFormMutation = useMutation({
    mutationFn: (formData) => api.post('/forms', formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms'])
      setShowCreateForm(false)
      setNewFormData({ name: '', slug: '', fields: [] })
      setExpandedFields(new Set())
    },
  })

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: `field_${newFormData.fields.length + 1}`,
      label: `Field ${newFormData.fields.length + 1}`,
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    }
    setNewFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }))
    setExpandedFields((prev) => new Set([...prev, newField.id]))
  }

  const updateField = (fieldId, updates) => {
    setNewFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      ),
    }))
  }

  const removeField = (fieldId) => {
    setNewFormData((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.id !== fieldId),
    }))
    setExpandedFields((prev) => {
      const next = new Set(prev)
      next.delete(fieldId)
      return next
    })
  }

  const toggleFieldExpand = (fieldId) => {
    setExpandedFields((prev) => {
      const next = new Set(prev)
      if (next.has(fieldId)) next.delete(fieldId)
      else next.add(fieldId)
      return next
    })
  }

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= newFormData.fields.length) return
    const newFields = [...newFormData.fields]
    ;[newFields[index], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[index],
    ]
    setNewFormData((prev) => ({ ...prev, fields: newFields }))
  }

  const deleteFormMutation = useMutation({
    mutationFn: (id) => api.delete(`/forms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms'])
    },
  })

  const filteredForms =
    forms?.data?.filter(
      (form) =>
        form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.slug?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Forms</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage contact forms and view submissions.
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Form
        </Button>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search forms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Create New Form
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false)
                setNewFormData({ name: '', slug: '', fields: [] })
                setExpandedFields(new Set())
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              createFormMutation.mutate({
                name: newFormData.name,
                slug: newFormData.slug?.trim() || undefined,
                settings: {
                  submit_button_text: 'Submit',
                  success_message: 'Thank you for your submission!',
                },
                schema: { fields: newFormData.fields },
              })
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <Input
                  value={newFormData.name}
                  onChange={(e) =>
                    setNewFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Form name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Slug
                </label>
                <Input
                  value={newFormData.slug}
                  onChange={(e) =>
                    setNewFormData((prev) => ({
                      ...prev,
                      slug: e.target.value,
                    }))
                  }
                  placeholder="Auto-generated from name"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to auto-generate from name
                </p>
              </div>
            </div>

            <div className="mt-6 border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium text-gray-900">
                  Form Fields
                </h4>
                <Button type="button" onClick={addField} size="sm">
                  <Plus className="mr-1 h-4 w-4" />
                  Add Field
                </Button>
              </div>

              {newFormData.fields.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-500">No fields added yet.</p>
                  <p className="text-sm text-gray-400">
                    Click "Add Field" to start building your form.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newFormData.fields.map((field, index) => {
                    const isExpanded = expandedFields.has(field.id)
                    return (
                      <div
                        key={field.id}
                        className="border border-gray-200 rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center space-x-3">
                            <GripVertical className="h-4 w-4 text-gray-400" />
                            <button
                              type="button"
                              onClick={() => toggleFieldExpand(field.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${getFieldTypeColor(
                                field.type
                              )}`}
                            >
                              {FIELD_TYPES.find((t) => t.name === field.type)
                                ?.label || field.type}
                            </span>
                            <span className="font-medium text-gray-900 text-sm">
                              {field.label || field.name}
                            </span>
                            {field.required && (
                              <span className="text-red-500 text-xs">*</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() => moveField(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveField(index, 'down')}
                              disabled={index === newFormData.fields.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => removeField(field.id)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-4 pb-4 pt-2 border-t border-gray-200 bg-white rounded-b-lg">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Field Name
                                </label>
                                <Input
                                  value={field.name}
                                  onChange={(e) =>
                                    updateField(field.id, {
                                      name: e.target.value,
                                    })
                                  }
                                  placeholder="field_name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Label
                                </label>
                                <Input
                                  value={field.label}
                                  onChange={(e) =>
                                    updateField(field.id, {
                                      label: e.target.value,
                                    })
                                  }
                                  placeholder="Field Label"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Field Type
                                </label>
                                <select
                                  value={field.type}
                                  onChange={(e) =>
                                    updateField(field.id, {
                                      type: e.target.value,
                                    })
                                  }
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                >
                                  {FIELD_TYPES.map((type) => (
                                    <option key={type.name} value={type.name}>
                                      {type.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex items-center pt-6">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    onChange={(e) =>
                                      updateField(field.id, {
                                        required: e.target.checked,
                                      })
                                    }
                                    className="rounded border-gray-300 text-primary-600"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">
                                    Required
                                  </span>
                                </label>
                              </div>
                            </div>

                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Placeholder
                              </label>
                              <Input
                                value={field.placeholder || ''}
                                onChange={(e) =>
                                  updateField(field.id, {
                                    placeholder: e.target.value,
                                  })
                                }
                                placeholder="Enter placeholder text"
                              />
                            </div>

                            {(field.type === 'select' ||
                              field.type === 'multiselect') && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Options (one per line)
                                </label>
                                <textarea
                                  value={field.options?.join('\n') || ''}
                                  onChange={(e) => {
                                    const options = e.target.value
                                      .split('\n')
                                      .filter((opt) => opt.trim())
                                    updateField(field.id, { options })
                                  }}
                                  rows={4}
                                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCreateForm(false)
                  setNewFormData({ name: '', slug: '', fields: [] })
                  setExpandedFields(new Set())
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createFormMutation.isPending}>
                {createFormMutation.isPending ? 'Creating...' : 'Create Form'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">Loading forms...</div>
          </div>
        ) : filteredForms.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">
              {searchTerm
                ? 'No forms found matching your search.'
                : 'No forms created yet.'}
            </div>
          </div>
        ) : (
          filteredForms.map((form) => (
            <div
              key={form.id}
              className="bg-white shadow rounded-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Mail className="h-8 w-8 text-gray-400" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {form.name}
                      </h3>
                      <p className="text-sm text-gray-500">{form.slug}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/forms/${form.id}/submissions`)}
                      className="text-gray-400 hover:text-gray-600"
                      title="View Submissions"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/forms/${form.id}/edit`)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (
                          confirm('Are you sure you want to delete this form?')
                        ) {
                          deleteFormMutation.mutate(form.id)
                        }
                      }}
                      className="text-red-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {form.schema?.fields?.length || 0} fields
                  </div>
                  <div className="text-xs text-gray-500">
                    {form.submissions_count || 0} submissions
                  </div>
                </div>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      form.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {form.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
